/* eslint-disable react/display-name */
/* eslint-disable react/prop-types*/
import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import {
  Grid,
  Typography,
  InputAdornment,
  ListItemText,
} from '@material-ui/core'
import { DateTimePicker } from '@material-ui/pickers'
import { DateRange } from '@material-ui/icons'

import { useTranslation } from 'react-i18next'
import parseError from 'helpers/errorHelpers'

import BaseDialog from 'ui/BaseDialog'
import AutocompleteV2 from 'ui/v2/Autocomplete'
import { useQueryClient } from 'react-query'
import { useReaderContext } from './context/reader-context'

import { userRoleSelector } from 'store/auth/selectors'
import { ROLES } from 'constants/roles'
import { useSelector } from 'react-redux'
import useNotification from 'hooks/useNotification'

import { useSubSchoolSetting } from 'api/School/SubSchoolSettings/queries'
import { useTeacherClasses } from 'api/IndividualTeacher/Classes/queries'
import { useLevels } from 'api/School/Levels/queries'
import { useLessons } from 'api/School/Lessons/queries'
import { useCreateSchoolAssignment } from 'api/Package/SchoolAssignments/queries'

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 600,
    minHeight: 400,
  },
  dateStart: {
    color: theme.palette.common.digired,
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
}))

function ModalCreateAssignmentDialog({
  open = false,
  onClose = () => {},
  currentActivity = '',
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { addNotification } = useNotification()

  let userRole = useSelector(userRoleSelector)

  const IS_INDIVIDUAL_TEACHER = userRole === ROLES.INDTEACHER

  const currentSubSchool = useSelector((state) => state.auth.currentSubSchool)
  const SCHOOL_TYPE_2 = currentSubSchool?.type_id === 2
  const currentSchoolID = SCHOOL_TYPE_2
    ? currentSubSchool?.user_id
    : currentSubSchool?.id
  const yearID = useSelector((state) => state.auth.currentSchoolYear?.id)

  const [level, setLevel] = React.useState(null)
  const [lesson, setLesson] = React.useState(null)
  const [selectedTeacherClass, setSelectedTeacherClass] = React.useState([])
  const [dataLessons, setLessons] = React.useState([])
  const [lessonError, setLessonError] = React.useState('')

  const {
    readerMode: { IS_DIGIPUB, IS_DIGIIEBOOK },
    info: { currentActivityWithID },
  } = useReaderContext()

  let { data: lessonSetting } = useSubSchoolSetting(2, {
    enabled: !IS_INDIVIDUAL_TEACHER,
  })

  const handleChangeLevel = (e, value) => {
    setLevel(value)
    setLesson(null)
    setLessonError('')
    lessons.refetch()
  }
  const handleChangeLesson = (e, value) => {
    if (value?.length > 10) {
      setLessonError(t('assignments.assignmentCreate.step1.maxSelection'))
      return
    }
    setLessonError('')
    setLessons(value)
  }
  const handleChangeIndTeacherClass = (e, value) => {
    setSelectedTeacherClass(value)
  }

  const [deadline, setDeadline] = React.useState(null)

  const handleDeadline = (date) => {
    setDeadline(date)
  }

  const teacherClasses = useTeacherClasses(null, {
    queryFilters: {
      filters: [
        { by: 'sort_by', value: 'created.desc' },
        { by: 'user_id', value: currentSchoolID },
      ],
    },
    queryOptions: {
      enabled: IS_INDIVIDUAL_TEACHER,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(
            error,
            t('assignments.assignmentCreate.step1.errorParsingClasses')
          ),
        })
      },
    },
  })

  const levelId = level?.id

  const levels = useLevels(null, {
    queryFilters: {
      filters: [
        { by: 'per_page', value: 0 },
        { by: 'Subject.sub_school_id', value: currentSchoolID },
        { by: 'active', value: 1 },
      ],
    },
    queryOptions: {
      enabled: !IS_INDIVIDUAL_TEACHER,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(
            error,
            t('assignments.assignmentCreate.step1.errorParsingLevels')
          ),
        })
      },
    },
  })

  let lessonFilters = {
    filters: [
      { by: 'Class.level_id', value: levelId },
      { by: 'Class.school_year_id', value: yearID },
    ],
  }
  if (!lessonSetting?.active) {
    lessonFilters.includeModels = ['Users']
  }

  const lessons = useLessons(null, {
    queryFilters: lessonFilters,
    queryOptions: {
      enabled: !!levelId && !!yearID && !IS_INDIVIDUAL_TEACHER,
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(
            error,
            t('assignments.assignmentCreate.step1.errorParsingLessons')
          ),
        })
      },
    },
  })

  const createAssignment = useCreateSchoolAssignment({
    queryOptions: {
      onSuccess: () => {
        addNotification({
          type: 'success',
          message: t(
            `assignments.assignmentCreate.step1.successAdding.${'singular'}`
          ),
        })
        setLevel(null)
        setDeadline(null)
        // setLesson([])
        setLessons([])
        setLessonError('')
        setSelectedTeacherClass([])
        queryClient.invalidateQueries('schoolAssignments')
        onClose()
        queryClient.invalidateQueries('schoolAssignments')
      },
      onError: (error) => {
        addNotification({
          type: 'error',
          message: parseError(
            error,
            t('assignments.assignmentCreate.step1.errorCreating')
          ),
        })
      },
    },
  })

  const BUTTON_DISABLED = IS_INDIVIDUAL_TEACHER
    ? !selectedTeacherClass?.length || !deadline || createAssignment.isLoading
    : !deadline || createAssignment.isLoading || !dataLessons?.length

  const onSubmit = () => {
    if (IS_DIGIIEBOOK) {
      if (IS_INDIVIDUAL_TEACHER) {
        createAssignment.mutate({
          payload: {
            type_id: 2,
            sub_school_id: currentSchoolID,
            Classes: selectedTeacherClass.map((c) => ({ class_id: c.id })),
            deadline,
            activity_ids: [currentActivityWithID?.id],
          },
        })
      } else {
        createAssignment.mutate({
          payload: {
            type_id: 1,
            Classes: dataLessons.map((lesson) => ({
              lesson_id: lesson.id,
              class_id: lesson.class_id,
            })),
            deadline,
            sub_school_id: currentSchoolID,
            activity_ids: [currentActivityWithID?.id],
          },
        })
      }
    } else if (IS_DIGIPUB) {
      if (IS_INDIVIDUAL_TEACHER) {
        createAssignment.mutate({
          payload: {
            type_id: 2,
            sub_school_id: currentSchoolID,
            Classes: selectedTeacherClass.map((c) => ({ class_id: c.id })),
            deadline,
            activity_ids: [currentActivity.digi_activity_id],
          },
        })
      } else {
        createAssignment.mutate({
          payload: {
            type_id: 1,
            Classes: dataLessons.map((lesson) => ({
              lesson_id: lesson.id,
              class_id: lesson.class_id,
            })),
            deadline,
            sub_school_id: currentSchoolID,
            activity_ids: [currentActivity.digi_activity_id],
          },
        })
      }
    } else {
      if (IS_INDIVIDUAL_TEACHER) {
        createAssignment.mutate({
          payload: {
            type_id: 2,
            sub_school_id: currentSchoolID,
            Classes: selectedTeacherClass.map((c) => ({ class_id: c.id })),
            // class_id: selectedTeacherClass.id,
            deadline,
            activity_ids: [currentActivity.activityId],
          },
        })
      } else {
        createAssignment.mutate({
          payload: {
            type_id: 1,
            // lesson_id: lesson.id,
            // class_id: lesson.class_id,
            Classes: dataLessons.map((lesson) => ({
              lesson_id: lesson.id,
              class_id: lesson.class_id,
            })),
            deadline,
            sub_school_id: currentSchoolID,
            activity_ids: [currentActivity.activityId],
          },
        })
      }
    }
  }

  return (
    <BaseDialog
      classes={{
        paper: classes.dialogPaper,
      }}
      keepMounted
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      primaryActionProps={{
        color: 'primary',
        label: 'ok',
      }}
      isLoading={createAssignment.isLoading}
      disabled={BUTTON_DISABLED}
      content={
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Typography
              component="span"
              display="block"
              color="primary"
              align="center"
              variant="h4"
            >
              {t('reader.appbar.assignmentDialog.title')}
            </Typography>
          </Grid>
          <Grid
            container
            display="block"
            direction="row"
            justifyContent="center"
            align="center"
            alignItems="stretch"
            className={classes.column3Wrapper}
            xs={10}
            item
          >
            <>
              {IS_INDIVIDUAL_TEACHER ? null : (
                <Grid item xs={12} className={classes.marginTop}>
                  <AutocompleteV2
                    label={t(
                      'assignments.assignmentCreate.step1.headCells.level'
                    )}
                    placeholder={t(
                      'assignments.assignmentCreate.step1.headCells.level'
                    )}
                    options={levels?.data?.items ?? []}
                    error={levels.error}
                    loading={levels.isLoading}
                    onChange={handleChangeLevel}
                    getOptionLabel={(option) => option?.title}
                    value={level}
                    renderListItemText={(option) => (
                      <ListItemText
                        primary={option?.title}
                        secondary={option?.Subject.title}
                      />
                    )}
                  />
                </Grid>
              )}
              {IS_INDIVIDUAL_TEACHER ? (
                <Grid item xs={12} className={classes.marginTop}>
                  <AutocompleteV2
                    label={t(
                      'assignments.assignmentCreate.step1.headCells.class'
                    )}
                    placeholder={t(
                      'assignments.assignmentCreate.step1.headCells.class'
                    )}
                    options={teacherClasses?.data?.items ?? []}
                    loading={teacherClasses.isLoading}
                    error={teacherClasses.error}
                    onChange={handleChangeIndTeacherClass}
                    value={selectedTeacherClass}
                    getOptionLabel={(option) => option?.name}
                    multiple
                    disableCloseOnSelect={true}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} className={classes.marginTop}>
                  <AutocompleteV2
                    label={
                      lessonSetting?.active
                        ? t(
                            'assignments.assignmentCreate.step1.headCells.lesson'
                          )
                        : t(
                            'assignments.assignmentCreate.step1.headCells.class'
                          )
                    }
                    placeholder={
                      lessonSetting?.active
                        ? t(
                            'assignments.assignmentCreate.step1.headCells.lesson'
                          )
                        : t(
                            'assignments.assignmentCreate.step1.headCells.class'
                          )
                    }
                    disabled={!level}
                    disableCloseOnSelect={true}
                    multiple
                    options={lessons?.data?.items ?? []}
                    error={lessons.error}
                    helperText={lessonError}
                    loading={lessons.isLoading}
                    onChange={handleChangeLesson}
                    value={dataLessons}
                    getOptionLabel={(option) =>
                      lessonSetting?.active
                        ? option.title
                        : `${option.Class.title} - ${
                            option.User?.full_name
                              ? option.User?.full_name
                              : option.User?.username
                          }`
                    }
                    renderListItemText={(option) => (
                      <ListItemText
                        primary={
                          lessonSetting?.active
                            ? option?.title
                            : `${option?.Class?.title} - ${
                                option?.User?.full_name
                                  ? option?.User?.full_name
                                  : option?.User?.username
                              }`
                        }
                        secondary={
                          lessonSetting?.active
                            ? null
                            : option?.User?.full_name
                            ? option?.User?.full_name
                            : t('assignments.assignmentCreate.step1.errorName')
                        }
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12} className={classes.marginTop}>
                <DateTimePicker
                  margin="none"
                  variant="dialog"
                  format="fff"
                  label={t(
                    'assignments.assignmentCreate.step1.headCells.deadline'
                  )}
                  value={deadline}
                  onChange={handleDeadline}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <DateRange className={classes.dateStart} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          </Grid>
        </Grid>
      }
    />
  )
}

export default ModalCreateAssignmentDialog

ModalCreateAssignmentDialog.propTypes = {
  open: PropTypes.bool,
  currentActivity: PropTypes.string,
  onClose: PropTypes.func,
  remaining: PropTypes.number,
}
